/**
 * Deal Flow table — the SAME canonical leads as the card view (presentation only).
 */
import React from "react";
import { money } from "@/lib/format";
import { relativeAge } from "@/lib/dealFlow/metrics";
import { RatingStar } from "@/components/common/primitives";
import { SELLING_STATUS_LABEL, walmartSellingStatus } from "@/lib/dealFlow/adapter";

function dash(v) {
  if (v === null || v === undefined || v === "") return "—";
  return v;
}

function Thumb({ src, label }) {
  if (!src) {
    return (
      <div className="w-[38px] h-[38px] rounded-md border border-edge-subtle bg-surface-1 grid place-items-center text-[9px] text-ink-muted">
        {label}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="w-[38px] h-[38px] object-contain border border-edge-subtle rounded-md bg-white"
      loading="lazy"
    />
  );
}

export function DealFlowTable({ leads, onAnalyze, onOpen }) {
  if (!leads.length) {
    return <div className="py-16 text-center text-ink-muted">No leads clear these rules.</div>;
  }

  return (
    <div className="df-tscroll mx-0 mb-6">
      <table className="df-table">
        <thead>
          <tr>
            <th className="df-f2">Product</th>
            <th>Source</th>
            <th className="text-right">Source price</th>
            <th className="text-right">Walmart price</th>
            <th className="text-right">Spread</th>
            <th>Match</th>
            <th>Walmart status</th>
            <th className="text-right">Rating</th>
            <th>Discovered</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {leads.map((r) => {
            const age = relativeAge(r.createdAt);
            const selling = walmartSellingStatus(r.walmartAvailability);
            return (
              <tr
                key={r.id}
                className="border-b border-edge-subtle last:border-0 cursor-pointer hover:bg-surface-1"
                onClick={() => onOpen && onOpen(r)}
              >
                <td className="df-f2 df-seam">
                  <div className="flex items-center gap-2">
                    <Thumb src={r.walmartImage || r.sourceImage} label="—" />
                    <div className="min-w-0">
                      <span className="block text-[9.5px] font-semibold tracking-wide uppercase text-ink-muted mb-0.5">
                        {dash(r.sourceMerchant)} → Walmart
                      </span>
                      <span className="font-medium block max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap">
                        {dash(r.walmartTitle || r.sourceTitle)}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap">{dash(r.sourceMerchant)}</td>
                <td className="text-right tabular-nums">{money(r.sourcePrice, r.sourceCurrency)}</td>
                <td className="text-right tabular-nums">{money(r.walmartPrice, r.walmartCurrency)}</td>
                <td className="text-right tabular-nums">{money(r.spread, r.sourceCurrency)}</td>
                <td className="whitespace-nowrap">
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-surface-1 border border-edge-subtle">
                    {r.matchStatusLabel || "—"}
                  </span>
                </td>
                <td className="whitespace-nowrap">
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-surface-1 border border-edge-subtle">
                    {SELLING_STATUS_LABEL[selling] || "—"}
                  </span>
                </td>
                <td className="text-right whitespace-nowrap">
                  {r.rating != null ? (
                    <span className="inline-flex items-center gap-0.5">
                      <RatingStar />
                      <span className="tabular-nums">{r.rating.toFixed(1)}</span>
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="text-ink-muted text-xs whitespace-nowrap">{age}</td>
                <td>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onAnalyze(r); }}
                    disabled={!r.canAnalyze}
                    className="bg-navy-900 text-white rounded-lg px-4 py-1.5 text-[12.5px] font-semibold hover:bg-navy-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`deal-flow-table-analyze-${r.id}`}
                  >
                    Analyze
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
